if (!customElements.get('gh-release-preview')) {
  customElements.define('gh-release-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.running) return;
      this.running = true;
      this.release = Date.parse(this.dataset.releaseAt);
      this.started = performance.now();
      this.serverStart = Number(this.dataset.serverEpoch) * 1000;
      if (!Number.isFinite(this.release)) return;
      const date = new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', day: 'numeric', month: 'long' }).format(new Date(this.release));
      const time = new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(this.release));
      this.querySelector('[data-release-time]').textContent = `${this.dataset.label} ${time} on ${date} NZ time`;
      this.clock = this.querySelector('[data-release-clock]');
      this.clockParts = ['hours', 'minutes', 'seconds'].map(unit => this.querySelector(`[data-clock-${unit}]`));
      this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.onVisibility = () => { if (document.visibilityState === 'hidden') this.clockParts.forEach(part => part?._digits?.forEach(digit => this.settleDigit(digit))); if (this.fetching) return; clearTimeout(this.timer); this.tick(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.tick();
    }
    disconnectedCallback() { this.running = false; clearTimeout(this.timer); this.abort?.abort(); this.clockParts?.forEach(part => part?._digits?.forEach(digit => this.settleDigit(digit))); document.removeEventListener('visibilitychange', this.onVisibility); }
    createPanel(className, value) {
      const panel = document.createElement('span');
      panel.className = 'gh-flap__panel ' + className;
      const number = document.createElement('span');
      number.className = 'gh-flap__number';
      number.textContent = value;
      panel.append(number);
      return panel;
    }
    createDigit(value) {
      const digit = document.createElement('span');
      digit.className = 'gh-flap';
      digit._panels = [
        this.createPanel('gh-flap__top', value),
        this.createPanel('gh-flap__bottom', value),
        this.createPanel('gh-flap__top gh-flap__fold-top', value),
        this.createPanel('gh-flap__bottom gh-flap__fold-bottom', value)
      ];
      digit.append(...digit._panels);
      digit._value = value;
      digit._animations = [];
      digit._token = 0;
      this.settleDigit(digit);
      return digit;
    }
    settleDigit(digit) {
      digit._token += 1;
      digit._animations.forEach(animation => animation.cancel());
      digit._animations = [];
      digit._panels.forEach(panel => { panel.firstElementChild.textContent = digit._value; });
      digit._panels[2].hidden = true;
      digit._panels[3].hidden = true;
    }
    flipDigit(digit, value, animate, delay) {
      if (digit._value === value) return;
      this.settleDigit(digit);
      const oldValue = digit._value;
      digit._value = value;
      if (!animate) { this.settleDigit(digit); return; }
      const [top, bottom, foldTop, foldBottom] = digit._panels;
      top.firstElementChild.textContent = value;
      bottom.firstElementChild.textContent = oldValue;
      foldTop.firstElementChild.textContent = oldValue;
      foldBottom.firstElementChild.textContent = value;
      foldTop.hidden = false;
      foldBottom.hidden = false;
      const token = digit._token;
      const upper = foldTop.animate([
        { transform: 'rotateX(0deg)' },
        { transform: 'rotateX(-90deg)' }
      ], { duration: 160, delay, easing: 'cubic-bezier(.5,0,.9,.55)', fill: 'both' });
      const lower = foldBottom.animate([
        { transform: 'rotateX(90deg)', offset: 0 },
        { transform: 'rotateX(0deg)', offset: .85 },
        { transform: 'rotateX(-3deg)', offset: .94 },
        { transform: 'rotateX(0deg)', offset: 1 }
      ], { duration: 180, delay: delay + 160, easing: 'cubic-bezier(.15,.7,.35,1)', fill: 'both' });
      digit._animations = [upper, lower];
      Promise.all(digit._animations.map(animation => animation.finished)).then(() => {
        if (digit._token === token) this.settleDigit(digit);
      }).catch(() => {});
    }
    updateClock(now) {
      const remaining = Math.max(0, Math.ceil((this.release - now) / 1000));
      const values = [Math.floor(remaining / 3600), Math.floor(remaining / 60) % 60, remaining % 60];
      if (!this.clock || this.clockParts.some(part => !part)) return;
      this.clock.hidden = false;
      this.clock.setAttribute('aria-label', `${values[0]} hours, ${values[1]} minutes, ${values[2]} seconds until release`);
      this.clockParts.forEach((part, index) => {
        const value = String(values[index]).padStart(2, '0');
        if (part.dataset.clockValue === value) return;
        const animate = Boolean(part.dataset.clockValue) && !this.reduceMotion.matches && document.visibilityState === 'visible';
        if (!part._digits || part._digits.length !== value.length) {
          part._digits?.forEach(digit => this.settleDigit(digit));
          part._digits = [...value].map(character => this.createDigit(character));
          part.replaceChildren(...part._digits);
          part.setAttribute('aria-hidden', 'true');
        } else {
          [...value].forEach((character, digitIndex) => this.flipDigit(part._digits[digitIndex], character, animate, digitIndex * 25));
        }
        part.dataset.clockValue = value;
      });
    }
    async tick() {
      if (!this.isConnected) return;
      const now = this.serverStart + performance.now() - this.started;
      this.updateClock(now);
      if (now >= this.release && this.dataset.demo !== 'true' && document.visibilityState === 'visible' && !this.fetching) {
        this.fetching = true;
        const status = this.querySelector('[data-release-opening]');
        status.hidden = false;
        status.textContent = this.dataset.openingText;
        try {
          const url = new URL(this.dataset.productUrl, location.origin);
          url.searchParams.set('sections', this.dataset.sectionId);
          url.searchParams.set('_gh_release', String(Math.floor(now / 1000)));
          this.abort = new AbortController();
          const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', signal: this.abort.signal });
          if (response.ok) {
            const sections = await response.json();
            const html = sections[this.dataset.sectionId];
            if (typeof html === 'string' && html.trim()) {
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              if (!parsed.querySelector('gh-release-preview[data-gh-release-locked="true"]')) { location.reload(); return; }
            }
          }
        } catch {} finally { this.fetching = false; }
      }
      if (this.isConnected) this.timer = setTimeout(() => this.tick(), document.visibilityState === 'hidden' ? 30000 : now >= this.release ? 5000 : Math.max(50, 1000 - ((now - this.serverStart) % 1000)));
    }
  });
}
